/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import Dexie from "dexie";
import { trace } from "owa-trace";
import {
    AppSettingsServiceModel,
    IConflictDismissDbEntity,
    IItemsInTimeRangeSyncState,
    ILocalClientData,
    ILocateUserResponseEntity,
    IMemberEntity,
    IUserTeamSettingsEntity,
    INoteDbEntity,
    IOpenShiftDbEntity,
    IRoleEntity,
    IScheduleFiltersEntity,
    ISessionClientData,
    IShiftDbEntity,
    ITagEntity,
    ITeamDetailsSyncState,
    ITeamInfoEntity,
    ITeamMemberPermissionsDbEntity,
    ITeamPermissionDbEntity,
    ITeamSettingEntity,
    ITimeClockDbEntity,
    ITimeOffReasonEntity,
    IUserEntity,
    IUserPolicySettingsEntity,
    IUserSettingsEntity
} from "sh-models";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "sh-services";
import { ITenantDatabase } from "sh-services/data/ITenantDatabase";
import { TenantDatabase } from "sh-services/data/TenantDatabase";

import { CrossLocationOpenShiftDatabase } from "./CrossLocationOpenShiftDatabase";
import { CrossLocationOpenShiftDbEntity } from "./CrossLocationOpenShiftDbEntity";
import { CrossLocationShiftDatabase } from "./CrossLocationShiftDatabase";
import { CrossLocationShiftDbEntity } from "./CrossLocationShiftDbEntity";
import { CrossLocationTeamDatabase } from "./CrossLocationTeamDatabase";
import { CrossLocationTeamDbEntity } from "./CrossLocationTeamDbEntity";
import { ICrossLocationOpenShiftDatabase } from "./ICrossLocationOpenShiftDatabase";
import { ICrossLocationShiftDatabase } from "./ICrossLocationShiftDatabase";
import { ICrossLocationTeamDatabase } from "./ICrossLocationTeamDatabase";
import { INoteDatabase } from "./INoteDatabase";
import { IOpenShiftDatabase } from "./IOpenShiftDatabase";
import { IShiftDatabase } from "./IShiftDatabase";
import { ITeamDatabase } from "./ITeamDatabase";
import { IUserDatabase } from "./IUserDatabase";
import { IUserProfileDatabase } from "./IUserProfileDatabase";
import { MyShiftDatabase } from "./MyShiftDatabase";
import { NoteDatabase } from "./NoteDatabase";
import { OpenShiftDatabase } from "./OpenShiftDatabase";
import { ScheduleFiltersDatabase } from "./ScheduleFiltersDatabase";
import { ShiftDatabase } from "./ShiftDatabase";
import { TeamDatabase } from "./TeamDatabase";
import { UserDatabase } from "./UserDatabase";
import { UserProfileDatabase } from "./UserProfileDatabase";
import { UserProfileDbEntity } from "./UserProfileDbEntity";
import { logDetailedPerfEvent } from "../../sh-application/initialize";

// Dexie IndexedDb schema version
const DATABASE_SCHEMA_VERSION = 28;

/**
 * Dexie wrapper around IndexedDb
 */
export class AppDb extends Dexie {
    crossLocationTeams: Dexie.Table<CrossLocationTeamDbEntity, string>;
    crossLocationShifts: Dexie.Table<CrossLocationShiftDbEntity, string>;
    crossLocationOpenShifts: Dexie.Table<CrossLocationOpenShiftDbEntity, string>;
    teams: Dexie.Table<ITeamInfoEntity, string>;
    roles: Dexie.Table<IRoleEntity, string>;
    members: Dexie.Table<IMemberEntity, string>;
    tags: Dexie.Table<ITagEntity, string>;
    teamSettings: Dexie.Table<ITeamSettingEntity, string>;
    timeOffReasons: Dexie.Table<ITimeOffReasonEntity, string>;
    latestTimeClockEntry: Dexie.Table<ITimeClockDbEntity, string>;
    teamPermissions: Dexie.Table<ITeamPermissionDbEntity, string>;
    teamMemberPermissions: Dexie.Table<ITeamMemberPermissionsDbEntity, string>;
    // Cache AppSettingsServiceModel because the client model contains Moment() that is too complex to store in the DB
    appSettings: Dexie.Table<AppSettingsServiceModel, string>;
    userSettings: Dexie.Table<IUserSettingsEntity, string>;
    userPolicySettings: Dexie.Table<IUserPolicySettingsEntity, string>;
    userTeamSettings: Dexie.Table<IUserTeamSettingsEntity, string>;
    locateUserResponses: Dexie.Table<ILocateUserResponseEntity, string>;
    localClientData: Dexie.Table<ILocalClientData, string>;
    loggedInUsers: Dexie.Table<IUserEntity, string>;
    scheduleFilters: Dexie.Table<IScheduleFiltersEntity, string>;
    // Cache AppSettingsServiceModel because the client model contains Moment() that is too complex to store in the DB
    tenantAppSettings: Dexie.Table<AppSettingsServiceModel, string>;
    shifts: Dexie.Table<IShiftDbEntity, string>;
    myShifts: Dexie.Table<IShiftDbEntity, string>;
    notes: Dexie.Table<INoteDbEntity, string>;
    openShifts: Dexie.Table<IOpenShiftDbEntity, string>;
    conflictDismissals: Dexie.Table<IConflictDismissDbEntity, string>;
    // Teams + Session sync state
    sessionClientData: Dexie.Table<ISessionClientData, string>;
    teamDetailsSyncState: Dexie.Table<ITeamDetailsSyncState, string>;
    shiftsSyncState: Dexie.Table<IItemsInTimeRangeSyncState, string>;
    myShiftsSyncState: Dexie.Table<IItemsInTimeRangeSyncState, string>;
    notesSyncState: Dexie.Table<IItemsInTimeRangeSyncState, string>;
    openShiftsSyncState: Dexie.Table<IItemsInTimeRangeSyncState, string>;
    usersProfiles: Dexie.Table<UserProfileDbEntity, string>;

    constructor (databaseName: string) {
        super(databaseName);

        this.on("versionchange", ev => {
            // Default behavior for versionchange event is to close database connection.
            // Caller can override this behavior by doing db.on("versionchange", function(){ return false; });
            // Let's not block the other window from making it's delete() or open() call.
            // NOTE! This event is never fired in IE,Edge or Safari.

            // The reason for this is that your current web app obviously has old schema code that needs
            // to be updated. Another window got a newer version of the app and needs to upgrade DB but
            // your window is blocking it unless we close it here.
            if (ev.newVersion > 0) {
                trace.warn(`Another connection wants to upgrade database '${databaseName}'. Closing db now to resume the upgrade.`);
                this.close();
                // refresh the window (don't use window.location.reload because that breaks out the iframe on Edge)
                window.location.replace(window.location.href);
            } else {
                trace.warn(`Another connection wants to delete database '${databaseName}'. Closing db now to resume the delete request.`);
                this.close();
            }
          });

        // Schema Definition: Each key represents the name of an object store
        //      and each value represents the primary and secondary indexes.
        //      Only specify necessary indexes, not all properties
        // Note: If you add change the database schema, please increase the DATABASE_SCHEMA_VERSION.
        //      This will force the client to clear the db and recreate it fetching the data from the service
        this.version(DATABASE_SCHEMA_VERSION).stores({
            crossLocationShifts: "[teamId+userId+shift.id], [teamId+startTimestampMs+endTimestampMs]",
            crossLocationTeams: "[team.id+teamId], teamId",
            crossLocationOpenShifts: "[openShift.id+crossLocationId+teamId], [crossLocationId+teamId]",
            teams: 'id, groupId',
            roles: '[id+teamId], teamId',
            members: '[id+teamId], teamId',
            tags: '[id+teamId], teamId',
            teamSettings: '[id+teamId], teamId',
            timeOffReasons: '[id+teamId], teamId',
            appSettings: '[id+teamId], teamId',
            userSettings: 'userId',
            userPolicySettings: 'userId',
            userTeamSettings: 'teamId',
            locateUserResponses: 'userId',
            localClientData: 'userId',
            loggedInUsers: 'userId',
            scheduleFilters: 'teamId',
            tenantAppSettings: 'tenantId',
            // Items that need to be queried using a date range are stored with this compound index so we can query:
            // notes = await this.db.notes.where("[teamId+startTime+endTime]")
            // .between([fetchStartTimeStamp, fetchStartTimeStamp, teamId] /* lowerBound /, [fetchEndTimeStamp, fetchEndTimeStamp, teamId] / upperBound /, true / includeLower /, true/ includeUpper */)
            shifts: '[id+teamId], [startTime+endTime+teamId]',
            myShifts: '[id+teamId], [startTime+endTime+teamId]',
            notes: '[id+teamId], [startTime+endTime+teamId]',
            openShifts: '[id+teamId], [startTime+endTime+teamId]',
            conflictDismissals: '[id+teamId], [startTime+endTime+teamId]',
            // Team+Session Sync state tracking:
            // We use this to track data tied to a teams session
            // This data is periodically purged using by LRU to remove older session data
            sessionClientData: 'sessionId, lastUpdateTimestamp',
            teamDetailsSyncState: '[sessionId+teamId], sessionId, lastUpdateTimestamp',
            shiftsSyncState: '[sessionId+teamId], sessionId, lastUpdateTimestamp',
            myShiftsSyncState: '[sessionId+teamId], sessionId, lastUpdateTimestamp',
            notesSyncState: '[sessionId+teamId], sessionId, lastUpdateTimestamp',
            openShiftsSyncState: '[sessionId+teamId], sessionId, lastUpdateTimestamp',
            latestTimeClockEntry: 'teamId',
            teamPermissions: 'teamId',
            teamMemberPermissions: 'teamId',
            usersProfiles: "userId"
        });

        // Tell Dexie.js that these tables map to classes (instead of interfaces)
        this.appSettings.mapToClass(AppSettingsServiceModel);
        this.tenantAppSettings.mapToClass(AppSettingsServiceModel);
    }
}

/**
 * Factory class to create Database Acessors
 */
class AppDbFactory {

    private appDatabase: { [id: string]: AppDb; } = {};

    // TODO: Setup correct detection of supported browsers
    private isDbEnabled = true;
    protected static isDatabaseDisabled = false;

    /**
     * Get the AppDb for the logged in user (if the database is supported)
     * @param loggedInUserId Logged in user's ID
     */
    private async loadAppDb(loggedInUserId: string) {
        if (this.isDbEnabled && !ECSConfigService.isECSFeatureEnabled(ECSConfigKey.IndexedDbInitializationDisabled)) {
            trace.info('loadAppDb: fetching DB');
            let appDb = this.appDatabase[loggedInUserId];
                    if (!appDb && !AppDbFactory.isDatabaseDisabled) {
                        try {
                            appDb = (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableLoadAppDBTimeout) ? (await Promise.race([this.instantiateAppDb(loggedInUserId), this.timeOut()])) : (await this.instantiateAppDb(loggedInUserId))) as AppDb | null;
                            this.appDatabase[loggedInUserId] = appDb;
                            AppDbFactory.isDatabaseDisabled = !appDb;
                        } catch (e) {
                            InstrumentationService.trackException(e, "LoadAppDb", "AppDB.loadAppDB");
                        }
                    }

            trace.info('loadAppDb: returning DB');
            return appDb;
        } else {
            return null;
        }
    }

    private async instantiateAppDb(loggedInUserId: string) {
            trace.info('loadAppDb: deleting DB on upgrade');
            await this.deleteDatabaseOnUpgrade(loggedInUserId);
            trace.info('loadAppDb: complete deleting DB on upgrade');
            trace.info('loadAppDb: instantiating new DB');
            let newAppDb = new AppDb(loggedInUserId);
            trace.info('loadAppDb: complete instantiating new DB');
            logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "LoadAppDb success by instantiating new DB");
            return newAppDb;
    }

    private async timeOut() {
        return new Promise ( async (resolve) => { setTimeout(() => {
            trace.info('loadAppDb: timed out waiting for DB to load');
            logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "LoadAppDb timeout");
            resolve(null);
        }, ECSConfigService.getECSFeatureSetting(ECSConfigKey.LoadAppDBDurationMs));
    });
    }

    /**
     * Reset all data in the database when the version number is upgraded
     * @param loggedInUserId Logged in user's ID
     */
    private async deleteDatabaseOnUpgrade(loggedInUserId: string) {
        if (await Dexie.exists(loggedInUserId)) {
            // Load the database to check the current version
            const tempDb = await new Dexie(loggedInUserId).open();
            if (tempDb.verno < DATABASE_SCHEMA_VERSION) {
                // clear the database if it's has an older version
                trace.warn('Clearing the old database');
                await tempDb.delete();
            }
            tempDb.close();
        }
    }

    /**
     * Delete the database for the logged in user
     * @param loggedInUserId Logged in user's ID
     */
    public async deleteAppDb(loggedInUserId: string) {
        if (this.isDbEnabled && !ECSConfigService.isECSFeatureEnabled(ECSConfigKey.IndexedDbInitializationDisabled)) {
            // We delete the database if it has been loaded into memory
            let appDb = this.appDatabase[loggedInUserId];
            if (appDb) {
                await appDb.delete();
                delete this.appDatabase[loggedInUserId];
            }
        }
    }

    /**
     * Gets the cross-location team database (this creates the database if it doesn't exist).
     * @param loggedInUserId Logged in user's ID.
     * @returns The cross-location team database.
     */
    public async loadCrossLocationTeamDatabase(loggedInUserId: string): Promise<ICrossLocationTeamDatabase> {
        trace.info('loadCrossLocationTeamDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new CrossLocationTeamDatabase(appDb);
    }

    /**
     * Gets the cross-location open shift database (this creates the database if it doesn't exist).
     * @param loggedInUserId Logged in user's ID.
     * @returns The cross-location open shift database.
     */
    public async loadCrossLocationOpenShiftDatabase(loggedInUserId: string): Promise<ICrossLocationOpenShiftDatabase> {
        trace.info('loadCrossLocationOpenShiftDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new CrossLocationOpenShiftDatabase(appDb);
    }

    /**
     * Gets the cross-location shift database (this creates the database if it doesn't exist).
     * @param loggedInUserId Logged in user's ID.
     * @returns The cross-location shift database.
     */
    public async loadCrossLocationShiftDatabase(loggedInUserId: string): Promise<ICrossLocationShiftDatabase> {
        trace.info('loadCrossLocationShiftDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new CrossLocationShiftDatabase(appDb);
    }

    /**
     * Get the TeamDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadTeamDatabase(loggedInUserId: string): Promise<ITeamDatabase> {
        trace.info('loadTeamDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new TeamDatabase(appDb);
    }

    /**
     * Get the UserDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadUserDatabase(loggedInUserId: string): Promise<IUserDatabase> {
        trace.info('loadUserDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new UserDatabase(appDb);
    }

    /**
     * Get the TenantDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadTenantDatabase(loggedInUserId: string): Promise<ITenantDatabase> {
        trace.info('loadTenantDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new TenantDatabase(appDb);
    }

    /**
     * Get the ShiftDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadShiftsDatabase(loggedInUserId: string): Promise<IShiftDatabase> {
        trace.info('loadShiftsDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new ShiftDatabase(appDb);
    }

    /**
     * Get the MyShiftDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadMyShiftsDatabase(loggedInUserId: string): Promise<IShiftDatabase> {
        trace.info('loadMyShiftsDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new MyShiftDatabase(appDb);
    }

    /**
     * Get the NoteDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadNotesDatabase(loggedInUserId: string): Promise<INoteDatabase> {
        trace.info('loadNotesDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new NoteDatabase(appDb);
    }

    /**
     * Get the OpenShiftDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadOpenShiftsDatabase(loggedInUserId: string): Promise<IOpenShiftDatabase> {
        trace.info('loadOpenShiftsDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new OpenShiftDatabase(appDb);
    }

    /**
     * Get the ScheduleFiltersDatabase accessor (this creates the database if it doesn't exist)
     * @param loggedInUserId Logged in user's ID
     */
    public async loadScheduleFiltersDatabase(loggedInUserId: string): Promise<ScheduleFiltersDatabase> {
        trace.info('loadScheduleFiltersDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new ScheduleFiltersDatabase(appDb);
    }

    /**
     * Gets the user profile database (this creates the database if it doesn't exist).
     * @param loggedInUserId Logged in user's ID.
     * @returns The user profile database.
     */
    public async loadUserProfileDatabase(loggedInUserId: string): Promise<IUserProfileDatabase> {
        trace.info('loadUserProfileDatabase');
        const appDb = await this.loadAppDb(loggedInUserId);
        return new UserProfileDatabase(appDb);
    }

    /**
     * Delete all databases
     */
    public async deleteAllDatabases() {
        try {
            const databases = await Dexie.getDatabaseNames();
            for (let i = 0; i < databases.length; i++) {
                try {
                    const db = new Dexie(databases[i]);
                    await db.delete();
                } catch (deleteError) {
                    InstrumentationService.trackException(deleteError, "DeleteDb");
                }
            }
        } catch (loadingDbError) {
            InstrumentationService.trackException(loadingDbError, "deleteAllDatabases");
        }
    }
}

const dbSet = new AppDbFactory();
export default dbSet as AppDbFactory;